import {createElement} from 'react';
import type {ComponentType} from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import dynamic from 'next/dynamic';

const HeroBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "HeroBlock" */
        '../blocks/HeroBlock/HeroBlock'
    )).HeroBlock
);

const FeaturedWalksBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "FeaturedWalksBlock" */
        '../blocks/FeaturedWalksBlock/FeaturedWalksBlock'
    )).FeaturedWalksBlock
);

const CategoryFilterBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "CategoryFilterBlock" */
        '../blocks/CategoryFilterBlock/CategoryFilterBlock'
    )).CategoryFilterBlock
);

const AllWalksBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "AllWalksBlock" */
        '../blocks/AllWalksBlock/AllWalksBlock'
    )).AllWalksBlock
);

const NearbyWaypointsBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "NearbyWaypointsBlock" */
        '../blocks/NearbyWaypointsBlock/NearbyWaypointsBlock'
    )).NearbyWaypointsBlock
);

const TextBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "TextBlock" */
        '../blocks/TextBlock/TextBlock'
    )).TextBlock
);

const FaqBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "FaqBlock" */
        '../blocks/FaqBlock/FaqBlock'
    )).FaqBlock
);

const RowBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "RowBlock" */
        '../blocks/RowBlock/RowBlock'
    )).RowBlock
);

const WalkTeaserBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "WalkTeaserBlock" */
        '../blocks/WalkTeaserBlock/WalkTeaserBlock'
    )).WalkTeaserBlock
);

const WaypointTeaserBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "WaypointTeaserBlock" */
        '../blocks/WaypointTeaserBlock/WaypointTeaserBlock'
    )).WaypointTeaserBlock
);

const ImageBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "ImageBlock" */
        '../blocks/ImageBlock/ImageBlock'
    )).ImageBlock
);

const WalkPurchaseBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "WalkPurchaseBlock" */
        '../blocks/WalkPurchaseBlock/WalkPurchaseBlock'
    )).WalkPurchaseBlock
);

const WalkMapBlockComponent = dynamic(
    async () => (await import(
        /* webpackChunkName: "WalkMapBlock" */
        '../blocks/WalkMapBlock/WalkMapBlock'
    )).WalkMapBlock
);

const BlockMap: Record<string, ComponentType<any> | undefined> = {
    'all-walks-block': AllWalksBlockComponent,
    'category-filter-block': CategoryFilterBlockComponent,
    'faq-block': FaqBlockComponent,
    'featured-walks-block': FeaturedWalksBlockComponent,
    'hero-block': HeroBlockComponent,
    'image-block': ImageBlockComponent,
    'nearby-waypoints-block': NearbyWaypointsBlockComponent,
    'row-block': RowBlockComponent,
    'text-block': TextBlockComponent,
    'walk-map-block': WalkMapBlockComponent,
    'walk-purchase-block': WalkPurchaseBlockComponent,
    'walk-teaser-block': WalkTeaserBlockComponent,
    'waypoint-teaser-block': WaypointTeaserBlockComponent
};

interface BlockData {
    blockType: string;
    id?: string | null;
    [key: string]: unknown;
}

export type {BlockData};

/**
 * Props for the BlockRenderer component.
 */
interface ComponentProps {
    /** Array of block data objects from the Homepage global. */
    blocks: BlockData[] | null | undefined;
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     *
     * @default 'BlockRenderer'
     */
    testId?: string;
}

/**
 * Renders an array of Payload CMS blocks by mapping each block's `blockType`
 * to its corresponding React component.
 *
 * @param props        The component props.
 * @param props.blocks Array of block data from the Homepage global.
 * @param props.testId An optional test ID used for automation selectors. Defaults to `'BlockRenderer'`.
 * @returns A JSX element rendering all configured blocks.
 *
 * @example
 * ```tsx
 * <BlockRenderer blocks={homepage.blocks} />
 * ```
 */
const BlockRenderer = ({blocks, testId = 'BlockRenderer'}: ComponentProps) => {
    if (!blocks || blocks.length === 0) return null;

    return (
        <BlockList data-cy={testId}>
            {blocks.map(block => {
                const Component = BlockMap[block.blockType];

                if (!Component) return null;

                return createElement(Component, {
                    ...block,
                    key: block.id
                });
            })}
        </BlockList>
    );
};

BlockRenderer.displayName = 'BlockRenderer';

export {BlockRenderer};

const BlockList = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing(10)};
`;